// useReducer with Typescript:
// https://github.com/hswolff/youtube/blob/master/videos/why-i-love-usereducer/src/LoginUseReducerTypeScript.tsx

import React, { useReducer, ReactNode, useEffect } from 'react';
import { createContext } from 'use-context-selector';
import _ from 'lodash';

const initialState: ILabelState = {
  labels: {},
  initialLabels: {}, // if reset needed
  wildcards: {},
  config: {
    defaultLanguage: 'en-US',
    language: undefined,
    languages: ['en-US', 'pt-BR', 'es-ES'],
  },
  editing: {
    editMode: false,
    untrackedLabels: {},
  },
  setLanguage: () => {},
  toggleEditMode: () => {},
  addUntrackedLabel: () => {},
};

type ILanguage = 'en-US' | 'pt-BR' | 'es-ES' | string;

type ILabelState = {
  labels: object;
  initialLabels: object;
  wildcards: object;
  config: {
    defaultLanguage: ILanguage;
    language?: ILanguage;
    languages: ILanguage[];
  };
  editing: {
    editMode: boolean;
    untrackedLabels: object;
  };
  setLanguage: Function;
  toggleEditMode: Function;
  addUntrackedLabel: Function;
};

type ILabelAction =
  | { type: 'SET_LABELS'; payload: object }
  | { type: 'APPEND_LABELS'; payload: object }
  | { type: 'RESET_LABELS' }
  | { type: 'SET_WILDCARDS'; payload: object }
  | { type: 'SET_LANGUAGE'; payload: ILanguage }
  | { type: 'SET_LANGUAGES'; payload: ILanguage[] }
  | { type: 'DETECT_LANGUAGE' }
  | { type: 'TOGGLE_EDIT_MODE' }
  | { type: 'ADD_UNTRACKED_LABEL'; payload: { key: string; text: string } };

function labelReducer(state: ILabelState, action: ILabelAction): ILabelState {
  switch (action.type) {
    case 'SET_LABELS':
      return {
        ...state,
        labels: action.payload,
        initialLabels: action.payload,
      };
    case 'APPEND_LABELS':
      let updatedLabels = _.cloneDeep(state.initialLabels);

      try {
        _.forEach(_.keys(action.payload), (key) => {
          _.forEach(_.keys(_.get(action.payload, key)), (language) => {
            if (!_.includes(state.config.languages, language))
              throw new Error(`Language invalid: ${language}`);
            _.set(
              updatedLabels,
              `${key}.${language}`,
              _.get(action.payload, `${key}.${language}`)
            );
          });
        });
      } catch (err) {
        console.error(err);
      }

      return {
        ...state,
        labels: updatedLabels,
      };
    case 'RESET_LABELS':
      return {
        ...state,
        labels: state.initialLabels,
      };
    case 'SET_WILDCARDS':
      return {
        ...state,
        wildcards: action.payload,
      };
    case 'SET_LANGUAGE':
      return {
        ...state,
        config: {
          ...state.config,
          language: action.payload,
        },
      };
    case 'SET_LANGUAGES':
      return {
        ...state,
        config: {
          ...state.config,
          languages: action.payload,
        },
      };
    case 'DETECT_LANGUAGE':
      const browserLanguage = window?.navigator?.language;
      // check if browser language is an available language
      return {
        ...state,
        config: {
          ...state.config,
          // @ts-ignore
          language: _.includes(state.config.languages, browserLanguage)
            ? browserLanguage
            : state.config.defaultLanguage,
        },
      };
    case 'TOGGLE_EDIT_MODE':
      return {
        ...state,
        editing: {
          ...state.editing,
          editMode: !state.editing.editMode,
        },
      };
    case 'ADD_UNTRACKED_LABEL':
      const { key, text } = action.payload;
      return {
        ...state,
        editing: {
          ...state.editing,
          untrackedLabels: {
            ...state.editing.untrackedLabels,
            [key]: {
              [state.config.languages[0]]: text,
              [state.config.languages[1]]: null,
              [state.config.languages[2]]: null,
            },
          },
        },
      };
    default:
      return state;
  }
}

export const LabelContext = createContext(initialState as ILabelState);

export default function LabelProvider(props: {
  children?: ReactNode;
  labels?: object;
  wildcards?: object;
  customLabels?: object;
  language?: ILanguage;
  languages?: ILanguage[];
  onLanguageChanged?: Function;
}) {
  const [state, dispatch] = useReducer(labelReducer, initialState);

  // Actions
  // reminder: always dispatch; otherwise, is a SELECTOR, not an ACTION
  function setLabels(labels = {} as object) {
    dispatch({
      type: 'SET_LABELS',
      payload: labels,
    });
  }

  function appendLabels(labels = {} as object) {
    dispatch({
      type: 'APPEND_LABELS',
      payload: labels,
    });
  }

  function resetLabels() {
    dispatch({
      type: 'RESET_LABELS',
    });
  }

  function setWildcards(wildcards = {} as object) {
    dispatch({
      type: 'SET_WILDCARDS',
      payload: wildcards,
    });
  }

  function setLanguage(language: ILanguage) {
    dispatch({
      type: 'SET_LANGUAGE',
      payload: language,
    });
  }

  function setLanguages(languages: ILanguage[]) {
    dispatch({
      type: 'SET_LANGUAGES',
      payload: languages,
    });
  }

  function detectLanguage() {
    dispatch({
      type: 'DETECT_LANGUAGE',
    });
  }

  function toggleEditMode() {
    dispatch({
      type: 'TOGGLE_EDIT_MODE',
    });
  }

  function addUntrackedLabel(key: string, text: string) {
    dispatch({
      type: 'ADD_UNTRACKED_LABEL',
      payload: { key, text },
    });
  }

  useEffect(() => {
    !props.language && detectLanguage();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (props.language) {
      setLanguage(props.language);
      props.onLanguageChanged && props.onLanguageChanged(props.language);
    }
  }, [props.language]); // eslint-disable-line

  useEffect(() => {
    props.languages && setLanguages(props.languages);
  }, [props.languages]);

  useEffect(() => {
    !_.isEmpty(props.labels) && setLabels(props.labels);
  }, [props.labels]);

  useEffect(() => {
    !_.isEmpty(props.wildcards) && setWildcards(props.wildcards);
  }, [props.wildcards]);

  useEffect(() => {
    if (!_.isEmpty(props.customLabels)) {
      appendLabels(props.customLabels);
    } else {
      resetLabels();
    }
  }, [props.customLabels]);

  return (
    <LabelContext.Provider
      value={{
        ...state,
        setLanguage,
        addUntrackedLabel,
        toggleEditMode,
      }}
    >
      {props.children}
    </LabelContext.Provider>
  );
}
